import { DecimalPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  output,
} from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Locator } from '@readium/shared';
import { EllipsisDirective } from '../../../directives/ellipsis/ellipsis';
import type { Highlight } from '../../../models/selections';
import { ReaderNavigatorService } from '../../../services/readium/navigator/reader-navigator';
import { HighlightsService } from '../../../services/selections/highlights';
import { SelectionsService } from '../../../services/selections/selections';
import { ToastService } from '../../../services/toast/toast';

@Component({
  selector: 'nr-notes',
  imports: [TranslatePipe, EllipsisDirective, DecimalPipe],
  templateUrl: './notes.html',
  styleUrl: './notes.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class NotesComponent {
  private static readonly INSTANCE_ID_LENGTH = 8;

  private readonly highlightsService = inject(HighlightsService);
  private readonly selectionsService = inject(SelectionsService);
  private readonly readerNavigatorService = inject(ReaderNavigatorService);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);
  private readonly instanceId = crypto.randomUUID().slice(0, NotesComponent.INSTANCE_ID_LENGTH);

  // A highlight opened via someone else's shared link is only shown in the text, never listed here.
  readonly notes = computed(() => this.highlightsService.notes().filter((note) => !note.isShared));
  readonly nrNavigated = output();
  readonly colorMap = this.highlightsService.colorMap;

  triggerId(noteId: string): string {
    return `note-menu-${this.instanceId}-${noteId}`;
  }

  navigateToNote(highlight: Highlight): void {
    const noteLocator = Locator.deserialize(highlight.locator);
    if (!noteLocator) return;
    this.readerNavigatorService.goToLocator(noteLocator);
    this.nrNavigated.emit();
  }

  editNote(note: Highlight): void {
    this.highlightsService.noteIsDraft.set(false);
    this.highlightsService.noteToEdit.set(note);
    this.nrNavigated.emit();
  }

  copyNote(note: Highlight): void {
    navigator.clipboard.writeText(note.selectedText);
    this.toastService.success(String(this.translateService.instant('COPIED_TO_CLIPBOARD')));
    this.nrNavigated.emit();
  }

  shareNote(note: Highlight): void {
    this.nrNavigated.emit();
    void this.highlightsService.requestShare(note);
  }

  deleteNote(note: Highlight): void {
    this.selectionsService.removeSelection(note.id);
    this.highlightsService.removeHighlight(note);
    this.nrNavigated.emit();
  }
}
